import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Pencil, Square, Circle, ArrowRight, Type, Stamp, Trash2, Eye, EyeOff, Plus, Save, Undo2, Layers } from "lucide-react";
import { toast } from "sonner";

const TOOLS = [
  { id: "freehand", icon: Pencil, label: "Freehand" },
  { id: "rect", icon: Square, label: "Rectangle" },
  { id: "circle", icon: Circle, label: "Circle" },
  { id: "arrow", icon: ArrowRight, label: "Arrow" },
  { id: "text", icon: Type, label: "Text" },
  { id: "stamp", icon: Stamp, label: "Stamp" },
];

const STAMP_TYPES = [
  { value: "APPROVED", color: "#10B981" },
  { value: "REJECTED", color: "#DC2626" },
  { value: "VOID", color: "#64748B" },
  { value: "HOLD", color: "#F59E0B" },
  { value: "PRELIMINARY", color: "#2563EB" },
  { value: "FOR REVIEW", color: "#D97706" },
];

const COLORS = ["#DC2626", "#F59E0B", "#10B981", "#2563EB", "#7C3AED", "#0F172A"];

export default function MarkupEditor({ open, onClose, blueprint }) {
  const canvasRef = useRef(null);
  const [layers, setLayers] = useState([]);
  const [activeLayer, setActiveLayer] = useState(null);
  const [tool, setTool] = useState("freehand");
  const [color, setColor] = useState("#DC2626");
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [stampType, setStampType] = useState("APPROVED");
  const [textInput, setTextInput] = useState("");
  const [annotations, setAnnotations] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [currentPath, setCurrentPath] = useState([]);
  const [layerName, setLayerName] = useState("");
  const [showLayerPanel, setShowLayerPanel] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetchLayers = useCallback(async () => {
    if (!blueprint?.id) return;
    try {
      const resp = await axios.get(`${API}/blueprints/${blueprint.id}/markups`, { headers: getAuthHeaders() });
      setLayers(resp.data || []);
      if (resp.data.length > 0 && !activeLayer) {
        setActiveLayer(resp.data[0].id);
        setAnnotations(resp.data[0].annotations || []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [blueprint?.id]);

  useEffect(() => {
    if (open) fetchLayers();
  }, [open, fetchLayers]);

  // Redraw canvas when annotations change
  useEffect(() => {
    redrawCanvas();
  }, [annotations, layers, activeLayer]);

  const getVisibleAnnotations = () => {
    const visible = [];
    layers.forEach((l) => {
      if (l.visible !== false) {
        const anns = l.id === activeLayer ? annotations : (l.annotations || []);
        anns.forEach((a) => visible.push({ ...a, layerColor: l.color }));
      }
    });
    return visible;
  };

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = "#F1F5F9";
    ctx.lineWidth = 0.5;
    for (let x = 0; x < canvas.width; x += 40) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
    }
    for (let y = 0; y < canvas.height; y += 40) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
    }

    const visibleAnns = getVisibleAnnotations();
    visibleAnns.forEach((ann) => drawAnnotation(ctx, ann));
  };

  const drawAnnotation = (ctx, ann) => {
    const c = ann.color || ann.layerColor || "#DC2626";
    const sw = ann.stroke_width || 2;
    ctx.strokeStyle = c;
    ctx.fillStyle = c;
    ctx.lineWidth = sw;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const d = ann.data || {};

    switch (ann.type) {
      case "freehand":
        if (d.points && d.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(d.points[0].x, d.points[0].y);
          d.points.forEach((p) => ctx.lineTo(p.x, p.y));
          ctx.stroke();
        }
        break;
      case "rect":
        ctx.strokeRect(d.x || 0, d.y || 0, d.width || 0, d.height || 0);
        break;
      case "circle": {
        const r = d.radius || 0;
        ctx.beginPath();
        ctx.arc(d.cx || 0, d.cy || 0, r, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case "arrow": {
        const x1 = d.x1 || 0, y1 = d.y1 || 0, x2 = d.x2 || 0, y2 = d.y2 || 0;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        // Arrowhead
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const headLen = 12;
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
        ctx.stroke();
        break;
      }
      case "text":
        ctx.font = `bold ${Math.max(14, sw * 6)}px monospace`;
        ctx.fillText(ann.text || "", d.x || 0, d.y || 0);
        break;
      case "stamp": {
        const text = ann.text || "APPROVED";
        const sx = d.x || 0, sy = d.y || 0;
        ctx.save();
        ctx.translate(sx, sy);
        ctx.rotate(-0.15);
        ctx.font = "bold 24px monospace";
        const metrics = ctx.measureText(text);
        const tw = metrics.width + 20;
        const th = 36;
        ctx.strokeStyle = c;
        ctx.lineWidth = 3;
        ctx.setLineDash([4, 2]);
        ctx.strokeRect(-10, -28, tw, th);
        ctx.setLineDash([]);
        ctx.fillStyle = c;
        ctx.globalAlpha = 0.85;
        ctx.fillText(text, 0, 0);
        ctx.globalAlpha = 1;
        ctx.restore();
        break;
      }
      default:
        break;
    }
  };

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const handleMouseDown = (e) => {
    if (!activeLayer) { toast.error("Select or create a markup layer first"); return; }
    const pos = getCanvasPos(e);
    setDrawing(true);
    setStartPos(pos);

    if (tool === "freehand") {
      setCurrentPath([pos]);
    } else if (tool === "text") {
      const text = prompt("Enter annotation text:");
      if (text) {
        const ann = { id: crypto.randomUUID(), type: "text", data: { x: pos.x, y: pos.y }, text, color, stroke_width: strokeWidth };
        setAnnotations((prev) => [...prev, ann]);
      }
      setDrawing(false);
    } else if (tool === "stamp") {
      const ann = { id: crypto.randomUUID(), type: "stamp", data: { x: pos.x, y: pos.y }, text: stampType, color: STAMP_TYPES.find((s) => s.value === stampType)?.color || color, stroke_width: strokeWidth };
      setAnnotations((prev) => [...prev, ann]);
      setDrawing(false);
    }
  };

  const handleMouseMove = (e) => {
    if (!drawing || !startPos) return;
    const pos = getCanvasPos(e);

    if (tool === "freehand") {
      setCurrentPath((prev) => [...prev, pos]);
      // Draw live
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = "round";
      if (currentPath.length > 0) {
        const last = currentPath[currentPath.length - 1];
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      }
    } else {
      // Preview shape
      redrawCanvas();
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.setLineDash([4, 4]);
      if (tool === "rect") {
        ctx.strokeRect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y);
      } else if (tool === "circle") {
        const r = Math.sqrt((pos.x - startPos.x) ** 2 + (pos.y - startPos.y) ** 2);
        ctx.beginPath();
        ctx.arc(startPos.x, startPos.y, r, 0, Math.PI * 2);
        ctx.stroke();
      } else if (tool === "arrow") {
        ctx.beginPath();
        ctx.moveTo(startPos.x, startPos.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }
  };

  const handleMouseUp = (e) => {
    if (!drawing) return;
    setDrawing(false);
    const pos = getCanvasPos(e);

    let ann = null;
    if (tool === "freehand" && currentPath.length > 1) {
      ann = { id: crypto.randomUUID(), type: "freehand", data: { points: [...currentPath, pos] }, color, stroke_width: strokeWidth };
    } else if (tool === "rect" && startPos) {
      ann = { id: crypto.randomUUID(), type: "rect", data: { x: Math.min(startPos.x, pos.x), y: Math.min(startPos.y, pos.y), width: Math.abs(pos.x - startPos.x), height: Math.abs(pos.y - startPos.y) }, color, stroke_width: strokeWidth };
    } else if (tool === "circle" && startPos) {
      const r = Math.sqrt((pos.x - startPos.x) ** 2 + (pos.y - startPos.y) ** 2);
      ann = { id: crypto.randomUUID(), type: "circle", data: { cx: startPos.x, cy: startPos.y, radius: r }, color, stroke_width: strokeWidth };
    } else if (tool === "arrow" && startPos) {
      ann = { id: crypto.randomUUID(), type: "arrow", data: { x1: startPos.x, y1: startPos.y, x2: pos.x, y2: pos.y }, color, stroke_width: strokeWidth };
    }

    if (ann) {
      setAnnotations((prev) => [...prev, ann]);
    }
    setCurrentPath([]);
    setStartPos(null);
  };

  const handleUndo = () => {
    setAnnotations((prev) => prev.slice(0, -1));
  };

  const handleSave = async () => {
    if (!activeLayer) return;
    try {
      await axios.put(`${API}/markups/${activeLayer}`, { annotations }, { headers: getAuthHeaders() });
      toast.success("Markup saved");
      fetchLayers();
    } catch { toast.error("Failed to save markup"); }
  };

  const handleCreateLayer = async () => {
    const name = layerName.trim() || `Layer ${layers.length + 1}`;
    try {
      const resp = await axios.post(`${API}/blueprints/${blueprint.id}/markups`, {
        name, color, annotations: [],
      }, { headers: getAuthHeaders() });
      setLayers((prev) => [resp.data, ...prev]);
      setActiveLayer(resp.data.id);
      setAnnotations([]);
      setLayerName("");
      toast.success(`Layer "${name}" created`);
    } catch { toast.error("Failed to create layer"); }
  };

  const selectLayer = (layer) => {
    // Save current annotations to the old layer in memory
    if (activeLayer) {
      setLayers((prev) => prev.map((l) => l.id === activeLayer ? { ...l, annotations } : l));
    }
    setActiveLayer(layer.id);
    setAnnotations(layer.annotations || []);
  };

  const toggleLayerVisibility = (layerId) => {
    setLayers((prev) => prev.map((l) => l.id === layerId ? { ...l, visible: !l.visible } : l));
  };

  const deleteLayer = async (layerId) => {
    if (!window.confirm("Delete this markup layer?")) return;
    try {
      await axios.delete(`${API}/markups/${layerId}`, { headers: getAuthHeaders() });
      setLayers((prev) => prev.filter((l) => l.id !== layerId));
      if (activeLayer === layerId) {
        setActiveLayer(null);
        setAnnotations([]);
      }
      toast.success("Layer deleted");
    } catch { toast.error("Failed to delete layer"); }
  };

  if (!open || !blueprint) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-[95vw] w-[1200px] max-h-[90vh] p-0" data-testid="markup-editor">
        <DialogHeader className="px-4 py-3 border-b border-[#E2E8F0]">
          <DialogTitle className="text-[#0F172A] text-sm font-heading">
            Digital Redline — {blueprint.original_filename}
          </DialogTitle>
          <DialogDescription className="text-[10px] text-[#64748B]">
            Draw annotations, add stamps, and manage markup layers.
          </DialogDescription>
        </DialogHeader>

        <div className="flex" style={{ height: "calc(90vh - 80px)" }}>
          {/* Toolbar */}
          <div className="w-12 border-r border-[#E2E8F0] bg-[#FAFAFA] flex flex-col items-center py-2 gap-1" data-testid="markup-toolbar">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTool(t.id)}
                className={`w-9 h-9 flex items-center justify-center transition-colors ${tool === t.id ? "bg-[#0F172A] text-white" : "text-[#64748B] hover:bg-[#E2E8F0]"}`}
                title={t.label}
                data-testid={`tool-${t.id}`}
              >
                <t.icon size={16} />
              </button>
            ))}
            <div className="border-t border-[#E2E8F0] w-full my-1" />
            <button onClick={handleUndo} className="w-9 h-9 flex items-center justify-center text-[#64748B] hover:bg-[#E2E8F0]" title="Undo" data-testid="tool-undo">
              <Undo2 size={16} />
            </button>
            <button onClick={handleSave} className="w-9 h-9 flex items-center justify-center text-[#10B981] hover:bg-[#DCFCE7]" title="Save" data-testid="tool-save">
              <Save size={16} />
            </button>
          </div>

          {/* Canvas Area */}
          <div className="flex-1 overflow-auto bg-[#F8FAFC] relative">
            {/* Tool options bar */}
            <div className="sticky top-0 z-10 bg-white border-b border-[#E2E8F0] px-3 py-1.5 flex items-center gap-3 text-xs" data-testid="tool-options">
              <span className="text-[10px] font-bold uppercase text-[#94A3B8]">Color:</span>
              <div className="flex gap-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`w-5 h-5 border-2 ${color === c ? "border-[#0F172A] scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    data-testid={`color-${c.replace("#", "")}`}
                  />
                ))}
              </div>
              <span className="text-[10px] font-bold uppercase text-[#94A3B8] ml-2">Width:</span>
              <input
                type="range" min="1" max="8" value={strokeWidth}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
                className="w-16 h-4" data-testid="stroke-width"
              />
              <span className="font-mono text-[#0F172A]">{strokeWidth}px</span>
              {tool === "stamp" && (
                <>
                  <span className="text-[10px] font-bold uppercase text-[#94A3B8] ml-2">Stamp:</span>
                  <select
                    value={stampType}
                    onChange={(e) => setStampType(e.target.value)}
                    className="border border-[#E2E8F0] px-2 py-1 text-xs"
                    data-testid="stamp-select"
                  >
                    {STAMP_TYPES.map((s) => (
                      <option key={s.value} value={s.value}>{s.value}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
            <div className="p-4 flex items-center justify-center" style={{ minHeight: "500px" }}>
              <canvas
                ref={canvasRef}
                width={960}
                height={680}
                className="border border-[#E2E8F0] bg-white cursor-crosshair shadow-sm"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={() => { if (drawing) handleMouseUp({ clientX: 0, clientY: 0, target: canvasRef.current }); }}
                data-testid="markup-canvas"
              />
            </div>
          </div>

          {/* Layers Panel */}
          {showLayerPanel && (
            <div className="w-56 border-l border-[#E2E8F0] bg-white flex flex-col" data-testid="layers-panel">
              <div className="px-3 py-2 border-b border-[#E2E8F0] flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#64748B] flex items-center gap-1"><Layers size={12} /> Layers</span>
                <button onClick={() => setShowLayerPanel(false)} className="text-[#94A3B8] hover:text-[#0F172A]">&times;</button>
              </div>
              {/* Create new layer */}
              <div className="px-3 py-2 border-b border-[#E2E8F0]">
                <div className="flex gap-1">
                  <Input
                    value={layerName}
                    onChange={(e) => setLayerName(e.target.value)}
                    placeholder="Layer name..."
                    className="rounded-none text-[10px] h-7 border-[#E2E8F0]"
                    data-testid="new-layer-name"
                  />
                  <Button size="sm" onClick={handleCreateLayer} className="rounded-none bg-[#2563EB] text-white h-7 px-2" data-testid="create-layer-btn">
                    <Plus size={12} />
                  </Button>
                </div>
              </div>
              {/* Layer list */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-xs text-[#94A3B8]">Loading...</div>
                ) : layers.length === 0 ? (
                  <div className="p-4 text-center text-xs text-[#94A3B8]">No layers yet. Create one above.</div>
                ) : (
                  layers.map((l) => (
                    <div
                      key={l.id}
                      className={`px-3 py-2 border-b border-[#F1F5F9] flex items-center gap-2 cursor-pointer transition-colors ${activeLayer === l.id ? "bg-[#EFF6FF]" : "hover:bg-[#FAFAFA]"}`}
                      onClick={() => selectLayer(l)}
                      data-testid={`layer-${l.id}`}
                    >
                      <div className="w-3 h-3 border" style={{ backgroundColor: l.color, borderColor: l.color }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-medium text-[#0F172A] truncate">{l.name}</p>
                        <p className="text-[9px] text-[#94A3B8]">{(l.id === activeLayer ? annotations : l.annotations || []).length} annotations · {l.created_by_name}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(l.id); }}
                        className="text-[#94A3B8] hover:text-[#0F172A]"
                        data-testid={`toggle-layer-${l.id}`}
                      >
                        {l.visible !== false ? <Eye size={12} /> : <EyeOff size={12} />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteLayer(l.id); }}
                        className="text-[#94A3B8] hover:text-[#DC2626]"
                        data-testid={`delete-layer-${l.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          {!showLayerPanel && (
            <button
              onClick={() => setShowLayerPanel(true)}
              className="absolute right-2 top-16 bg-white border border-[#E2E8F0] p-1.5 text-[#64748B] hover:text-[#0F172A] shadow-sm"
              data-testid="show-layers-btn"
            >
              <Layers size={14} />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
